import { invalidatedProtocolsOf } from "@repo/api";
import { queueStrategy, type Transaction } from "@tanstack/db";

import { CascadedWriteError, markWriteErrorReported, writeErrorMessage } from "./write-errors";

export const outbox = queueStrategy();
let pending = 0;
export const pendingWrites = () => pending;
const guard = (event: BeforeUnloadEvent) => {
  event.preventDefault();
  event.returnValue = "";
};
const active = new Set<Transaction>();
const listeners = new Set<(message: string) => void>();
export function onWriteError(listener: (message: string) => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
export function reportWriteError(
  transaction: Transaction,
  error: unknown,
  options: { cascades: boolean } = { cascades: true },
): void {
  // The catalog editor names each protocol in a dialog; a toast would only repeat it.
  if (invalidatedProtocolsOf(error)) {
    markWriteErrorReported(error);
    return;
  }
  const cascades = options.cascades && pendingConflicts(transaction).length > 0;
  const base = writeErrorMessage(error);
  const message = `${base}${cascades ? " Later changes to that row were undone too." : ""}`;
  markWriteErrorReported(error);
  for (const listener of listeners) listener(message);
}
function pendingConflicts(transaction: Transaction): Transaction[] {
  const keys = new Set(transaction.mutations.map((mutation) => mutation.globalKey));
  return [...active].filter(
    (candidate) =>
      candidate !== transaction &&
      candidate.state === "pending" &&
      candidate.mutations.some((mutation) => keys.has(mutation.globalKey)),
  );
}

export function markCascadedWrites(transaction: Transaction, cause: unknown): void {
  // TanStack secondary rollback rejects with the candidate's existing error.
  for (const candidate of pendingConflicts(transaction)) {
    const error = new CascadedWriteError(cause);
    markWriteErrorReported(error);
    candidate.error = { message: error.message, error };
  }
}

export function trackWrite(transaction: Transaction): void {
  active.add(transaction);
  const promise = transaction.isPersisted.promise;
  if (pending++ === 0 && typeof window !== "undefined") window.addEventListener("beforeunload", guard);
  const settled = () => {
    active.delete(transaction);
    if (--pending === 0 && typeof window !== "undefined") window.removeEventListener("beforeunload", guard);
  };
  void promise.then(settled, settled);
}

interface QueuedSave {
  owner: object;
  persist: () => Promise<unknown>;
  result?: Promise<unknown>;
  key?: string;
}
const queued = new Map<string, QueuedSave>();
const saves = new WeakMap<Transaction, QueuedSave>();
export function enqueueSave(
  transaction: Transaction,
  owner: object,
  persist: () => Promise<unknown>,
  coalesce: boolean,
): void {
  const keys = [...new Set(transaction.mutations.map((mutation) => mutation.globalKey))];
  const key = coalesce && keys.length === 1 ? keys[0] : undefined;
  const prior = key ? queued.get(key) : undefined;
  if (prior && prior.owner === owner) {
    prior.persist = persist;
    saves.set(transaction, prior);
  } else {
    for (const touched of keys) queued.delete(touched);
    const save: QueuedSave = { owner, persist, key };
    saves.set(transaction, save);
    if (key) queued.set(key, save);
  }
  void transaction.isPersisted.promise.then(
    () => {},
    () => {
      const save = saves.get(transaction);
      if (save?.key && queued.get(save.key) === save) queued.delete(save.key);
    },
  );
}
export async function persistQueuedSave(transaction: Transaction): Promise<void> {
  const save = saves.get(transaction);
  if (!save) throw new Error("Write is missing from the outbox");
  if (!save.result) {
    if (save.key && queued.get(save.key) === save) queued.delete(save.key);
    save.result = Promise.resolve().then(save.persist);
  }
  await save.result;
}
